import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";

const statusIcons = {
    completed: { icon: "\u2713", bg: "bg-green-500", title: "Paid" },
    sent: { icon: "\u2709", bg: "bg-blue-500", title: "Sent - Awaiting Payment" },
    processing: { icon: "\u23F3", bg: "bg-indigo-500", title: "Processing" },
    pending: { icon: "\u25CB", bg: "bg-yellow-400", title: "Pending" },
    failed: { icon: "\u2717", bg: "bg-red-500", title: "Failed" },
    skipped: { icon: "\u2014", bg: "bg-gray-400", title: "Skipped" },
};

function MonthTick({ execution }) {
    const config = statusIcons[execution.status] || statusIcons.pending;
    const monthLabel = moment(execution.scheduled_for).format("MMM");
    const yearLabel = moment(execution.scheduled_for).format("YY");

    return (
        <div className="flex flex-col items-center" title={`${config.title} - ${moment(execution.scheduled_for).format("DD MMM YYYY")}${execution.amount ? ` - R${parseFloat(execution.amount).toFixed(2)}` : ''}`}>
            <div className={`w-7 h-7 rounded-full ${config.bg} text-white flex items-center justify-center text-xs font-bold`}>
                {config.icon}
            </div>
            <span className="text-[10px] text-gray-500 mt-1">{monthLabel}</span>
            <span className="text-[9px] text-gray-400">{yearLabel}</span>
        </div>
    );
}

function EmptyMonthTick({ date }) {
    const monthLabel = moment(date).format("MMM");
    const yearLabel = moment(date).format("YY");
    const isFuture = moment(date).isAfter(moment(), 'month');

    return (
        <div className="flex flex-col items-center" title={`${isFuture ? 'Upcoming' : 'No execution'} - ${moment(date).format("MMM YYYY")}`}>
            <div className={`w-7 h-7 rounded-full ${isFuture ? 'bg-gray-200' : 'bg-gray-100 border border-dashed border-gray-300'} text-gray-400 flex items-center justify-center text-xs`}>
                {isFuture ? '\u2022' : ''}
            </div>
            <span className="text-[10px] text-gray-400 mt-1">{monthLabel}</span>
            <span className="text-[9px] text-gray-300">{yearLabel}</span>
        </div>
    );
}

export default function ScheduledPayments() {
    const { current_booking } = useSelector((state) => state.booking);
    const [scheduledExecutions, setScheduledExecutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const openModal = async () => {
        setShowModal(true);
        if (!current_booking?.id) return;
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}scheduled-payments/booking/${current_booking.id}/executions`
            );
            setScheduledExecutions(response.data);
        } catch (error) {
            console.error("Failed to fetch scheduled executions:", error);
        } finally {
            setLoading(false);
        }
    };

    const buildTimeline = (schedule) => {
        const sp = schedule.scheduled_payment;
        const executions = schedule.executions || [];

        if (!sp.start_date || !sp.end_date) return executions;

        const start = moment(sp.start_date).startOf('month');
        const end = moment(sp.end_date).endOf('month');
        const months = [];
        const current = start.clone();

        while (current.isSameOrBefore(end, 'month')) {
            const exec = executions.find(e =>
                moment(e.scheduled_for).isSame(current, 'month')
            );
            months.push({
                date: current.clone(),
                execution: exec || null,
            });
            current.add(1, 'month');
        }

        return months;
    };

    return (
        <>
            <button
                onClick={openModal}
                className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-all duration-200 text-sm"
            >
                Scheduled Payments {scheduledExecutions.length > 0 ? `(${scheduledExecutions.length})` : ''}
            </button>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-lg font-bold text-gray-900">Scheduled Payments</h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="overflow-y-auto p-6 space-y-6" style={{ maxHeight: 'calc(85vh - 60px)' }}>
                            {loading ? (
                                <div className="flex justify-center items-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : scheduledExecutions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No scheduled payments for this booking.
                                </div>
                            ) : (
                                scheduledExecutions.map((schedule) => {
                                    const timeline = buildTimeline(schedule);
                                    const sp = schedule.scheduled_payment;
                                    const completedCount = schedule.executions.filter(e => e.status === 'completed').length;
                                    const sentCount = schedule.executions.filter(e => e.status === 'sent').length;
                                    const totalMonths = timeline.length;

                                    return (
                                        <div key={sp.id} className="border rounded-lg overflow-hidden">
                                            {/* Schedule Header */}
                                            <div className="bg-gray-50 px-4 py-3 border-b">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h3 className="text-sm font-semibold text-gray-900">{sp.name}</h3>
                                                        {sp.description && (
                                                            <p className="text-xs text-gray-500 mt-0.5">{sp.description}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                            sp.type === 'refund' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                            {sp.type}
                                                        </span>
                                                        {sp.payment_type && (
                                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 capitalize">
                                                                {sp.payment_type.replace(/_/g, ' ')}
                                                            </span>
                                                        )}
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                            sp.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {sp.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                                                    <span>Day: {sp.day_of_month}</span>
                                                    <span className="capitalize">{sp.payment_module}</span>
                                                    {!sp.is_dynamic_amount && sp.amount && (
                                                        <span>R{parseFloat(sp.amount).toFixed(2)}</span>
                                                    )}
                                                    {sp.is_dynamic_amount && (
                                                        <span className="text-purple-600 font-medium">Dynamic</span>
                                                    )}
                                                    <span className="ml-auto font-medium text-green-700">
                                                        {completedCount}/{totalMonths} paid
                                                    </span>
                                                    {sentCount > 0 && (
                                                        <span className="font-medium text-blue-700">
                                                            {sentCount} awaiting
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Monthly Timeline */}
                                            <div className="px-4 py-4">
                                                <div className="flex flex-wrap gap-3">
                                                    {timeline.map((month, idx) => (
                                                        <React.Fragment key={idx}>
                                                            {month.execution ? (
                                                                <MonthTick execution={month.execution} />
                                                            ) : (
                                                                <EmptyMonthTick date={month.date} />
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Detailed List */}
                                            {schedule.executions.length > 0 && (
                                                <div className="border-t px-4 py-3">
                                                    <details className="text-xs">
                                                        <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
                                                            View details ({schedule.executions.length} executions)
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {schedule.executions.map((exec) => (
                                                                <div key={exec.id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-gray-700">
                                                                            {moment(exec.scheduled_for).format("DD MMM YYYY")}
                                                                        </span>
                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${{
                                                                            completed: 'bg-green-100 text-green-800',
                                                                            sent: 'bg-blue-100 text-blue-800',
                                                                            processing: 'bg-indigo-100 text-indigo-800',
                                                                            pending: 'bg-yellow-100 text-yellow-800',
                                                                            failed: 'bg-red-100 text-red-800',
                                                                            skipped: 'bg-gray-100 text-gray-800',
                                                                        }[exec.status] || 'bg-gray-100 text-gray-800'}`}>
                                                                            {exec.status}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-3 text-gray-500">
                                                                        {exec.amount && (
                                                                            <span className="font-medium text-gray-700">R{parseFloat(exec.amount).toFixed(2)}</span>
                                                                        )}
                                                                        {exec.sent_at && (
                                                                            <span>Sent: {moment(exec.sent_at).format("DD MMM HH:mm")}</span>
                                                                        )}
                                                                        {exec.processed_at && (
                                                                            <span>Paid: {moment(exec.processed_at).format("DD MMM HH:mm")}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}

                            {/* Legend */}
                            <div className="flex flex-wrap gap-4 pt-4 border-t text-xs text-gray-500">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-4 h-4 rounded-full bg-green-500"></div>
                                    <span>Paid</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                                    <span>Sent</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-4 h-4 rounded-full bg-yellow-400"></div>
                                    <span>Pending</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-4 h-4 rounded-full bg-red-500"></div>
                                    <span>Failed</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-4 h-4 rounded-full bg-gray-200"></div>
                                    <span>Upcoming</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
